import { Icon, Modal } from '@deity-io/portal-ui';
import { EntityNameDescriptionFields } from 'components/FormFields';
import type { CreateEditModelInput } from 'contracts/interfaces';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  GetPackageVersionContentInfoDocument,
  useCreateWebServiceMutation,
  useUpdateWebServiceMutation
} from 'services/graphql/generated/hooks';
import type { GetWebServiceQuery } from 'services/graphql/generated/operations';
import { v4 } from 'uuid';
import * as Yup from 'yup';

interface CreateEditWebServiceModalProps {
  isOpen?: boolean;
  isEdit?: boolean;
  versionUuid: string;
  editWebService?: GetWebServiceQuery;
  setOpenState: (state: boolean) => void;
  onPackageCreated: () => void;
}

export const CreateEditWebServiceModal: FC<CreateEditWebServiceModalProps> = ({
  isOpen = false,
  setOpenState,
  onPackageCreated,
  isEdit,
  editWebService,
  versionUuid
}) => {
  const refetchQueries = {
    refetchQueries: [
      {
        query: GetPackageVersionContentInfoDocument,
        variables: {
          versionUuid
        }
      }
    ]
  };
  const [create, { data: createData, loading: createLoading }] = useCreateWebServiceMutation(refetchQueries);
  const [update, { data: updateData, loading: updateLoading }] = useUpdateWebServiceMutation(refetchQueries);

  const CreateWebServiceInputSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too short').max(100, 'Too long').required('This is a required field.'),
    description: Yup.string().min(2, 'Too short').max(255, 'Too long')
  });

  useEffect(() => {
    if (createData || updateData) {
      onPackageCreated();
      setOpenState(false);
    }
  }, [createData, updateData]);

  const createOrUpdate = (input: CreateEditModelInput) => {
    if (!input.description) {
      input.description = '';
    }

    return isEdit ? updateEndpoint(input) : createEndpoint(input);
  };

  const createEndpoint = (input: CreateEditModelInput) => {
    create({
      variables: {
        uuid: v4(),
        packageVersionUuid: versionUuid,
        name: input.name,
        description: input.description,
        schema: {
          type: 'rest',
          contentType: 'application/json',
          protocol: 'https'
        }
      }
    }).then();
  };

  const formik = useFormik({
    initialValues: {
      name: editWebService?.getWebService.name ?? '',
      description: editWebService?.getWebService.description ?? ''
    },
    validateOnBlur: true,
    validationSchema: CreateWebServiceInputSchema,
    onSubmit: values => createOrUpdate(values)
  });

  useEffect(() => {
    if (editWebService?.getWebService) {
      const { name, description } = editWebService.getWebService;
      formik.setValues({ name: name!, description: description! });
    }
  }, [editWebService?.getWebService]);

  const updateEndpoint = (input: CreateEditModelInput) => {
    update({
      variables: {
        packageVersionUuid: versionUuid,
        uuid: editWebService?.getWebService.uuid ?? '',
        name: input.name,
        description: input.description
      }
    }).then();
  };

  const confirmText = () => {
    if (!isEdit) {
      return `${createLoading ? 'Creating' : 'Create'} WebService`;
    }

    return `${updateLoading ? 'Updating' : 'Update'} WebService`;
  };

  if (isEdit && !editWebService) {
    return null!;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpenState(false)}
      label={`${isEdit ? 'Edit' : 'New'} WebService`}
      title="Enter a name and description."
      closeIcon={<Icon icon="close" />}
      confirmText={confirmText()}
      onConfirm={() => formik.handleSubmit()}
    >
      <form onSubmit={formik.handleSubmit}>
        <EntityNameDescriptionFields
          errors={formik.errors}
          values={formik.values}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
          entity="WebService"
        />
      </form>
    </Modal>
  );
};
